import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Assignment,
  Medication,
  Science,
  Image,
  Healing,
  LocalHospital,
  Note,
  Receipt,
  MonitorHeart,
  Visibility as ViewResultIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import axios from "axios";
import PrescriptionDialog from "../../../../Components/PrescriptionDialog";
import PrescriptionsList from "../../../../Components/PrescriptionsList";
import LabTestDialog from "./Forms/LabTestDialog";
import XRayDialog from "./Forms/XRayDialog";
import EchoDialog from "./Forms/EchoDialog";
import UltrasoundDialog from "./Forms/UltrasoundDialog";
import EncounterDialog from "./Forms/EncounterDialog";
import ClinicalNoteDialog from "./Forms/ClinicalNoteDialog";
import CostSummary from "./CostSummary";
import ZoomableImageDialog from "../../../../Components/GeneralComponents/ZoomableImageDialog/ZoomableImageDialog";
import ProfileSkeleton from "../../../../Components/GeneralComponents/profileSkeleton/ProfileSkeleton";

const TabPanel = ({ children, value, index }) => {
  return value === index && <Box sx={{ p: 2 }}>{children}</Box>;
};

const ComprehensiveProfile = ({
  patientId,
  ticketId = null,
  showFullHistory = false,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user?.user;

  // Debug: Log patientId to ensure it's being passed
  React.useEffect(() => {
    if (!patientId) {
      console.warn("ComprehensiveProfile: patientId is missing!", {
        patientId,
        ticketId,
      });
    }
  }, [patientId, ticketId]);

  const [selectedTab, setSelectedTab] = useState(0);
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [labDialogOpen, setLabDialogOpen] = useState(false);
  const [labOrderDetails, setLabOrderDetails] = useState({});
  const [labOrderLoading, setLabOrderLoading] = useState({});
  const [diagnosticDetails, setDiagnosticDetails] = useState({});
  const [diagnosticLoading, setDiagnosticLoading] = useState({});
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerSrc, setImageViewerSrc] = useState("");
  const [imageViewerTitle, setImageViewerTitle] = useState("");
  const [xrayDialogOpen, setXrayDialogOpen] = useState(false);
  const [echoDialogOpen, setEchoDialogOpen] = useState(false);
  const [ultrasoundDialogOpen, setUltrasoundDialogOpen] = useState(false);
  const [encounterDialogOpen, setEncounterDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  // Fetch unified EHR data
  const {
    data: ehrData,
    isLoading,
    refetch,
  } = useFetchObjects(
    patientId ? ["unified-ehr", patientId, ticketId] : null,
    patientId
      ? `patients/${patientId}/unified-ehr/${ticketId ? `?ticket_id=${ticketId}` : ""}`
      : "",
    token,
  );

  // Group data by ticket/encounter
  const groupedData = React.useMemo(() => {
    if (!ehrData?.timeline) return {};

    const groups = {};
    ehrData.timeline.forEach((item) => {
      // Get ticket identifier - try multiple sources
      const ticketKey =
        item.data?.ticket_number ||
        (item.data?.ticket_id ? `TICKET-${item.data.ticket_id}` : null) ||
        "no-ticket";

      // Get encounter identifier
      const encounterKey = item.data?.encounter_id
        ? `ENC-${item.data.encounter_id}`
        : item.type === "encounter"
          ? `ENC-${item.id}`
          : "no-encounter";

      if (!groups[ticketKey]) {
        groups[ticketKey] = {
          ticket_number: item.data?.ticket_number || ticketKey,
          ticket_id: item.data?.ticket_id,
          encounters: {},
        };
      }

      if (!groups[ticketKey].encounters[encounterKey]) {
        groups[ticketKey].encounters[encounterKey] = {
          encounter_id: encounterKey,
          items: [],
        };
      }

      groups[ticketKey].encounters[encounterKey].items.push(item);
    });

    return groups;
  }, [ehrData]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleAddPrescription = () => {
    setPrescriptionDialogOpen(true);
  };

  const handlePrescriptionAdded = () => {
    setPrescriptionDialogOpen(false);
    refetch();
  };

  const handlePrescriptionDialogClose = () => {
    setPrescriptionDialogOpen(false);
    refetch();
  };

  const apiBase = process.env.REACT_APP_API_URL || "";
  const mediaBase = apiBase.replace(/\/?api\/?$/, "");
  const toAbsoluteMediaUrl = (url) => {
    if (!url) return url;
    return url.startsWith("http")
      ? url
      : `${mediaBase}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const fetchLabOrderResults = async (orderId) => {
    if (labOrderDetails[orderId] || labOrderLoading[orderId]) return;
    setLabOrderLoading((prev) => ({ ...prev, [orderId]: true }));
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/laboratory-test-orders/${orderId}/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setLabOrderDetails((prev) => ({ ...prev, [orderId]: res.data }));
    } catch (err) {
      console.error("Failed to fetch lab order results", err);
    } finally {
      setLabOrderLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const getDiagnosticKey = (type, id) => `${type}-${id}`;
  const fetchDiagnosticResult = async (type, id) => {
    const key = getDiagnosticKey(type, id);
    if (diagnosticDetails[key] || diagnosticLoading[key]) return;
    setDiagnosticLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const endpoints = {
        xray: "patient-xray",
        ultrasound: "ultrasounds",
        echo: "patient-echo",
      };
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${endpoints[type]}/${id}/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setDiagnosticDetails((prev) => ({ ...prev, [key]: res.data }));
    } catch (err) {
      console.error("Failed to fetch diagnostic result", err);
    } finally {
      setDiagnosticLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  const tabs = [
    { label: t("profile.overview") || "Overview", icon: <Assignment /> },
    {
      label: t("profile.consultations") || "Consultations",
      icon: <LocalHospital />,
    },
    { label: t("profile.notes") || "Clinical Notes", icon: <Note /> },
    {
      label: t("profile.prescriptions") || "Prescriptions",
      icon: <Medication />,
    },
    { label: t("profile.labTests") || "Lab Tests", icon: <Science /> },
    { label: t("profile.xray") || "X-Ray", icon: <Image /> },
    { label: t("profile.ultrasound") || "Ultrasound", icon: <Image /> },
    { label: t("profile.echo") || "Echo", icon: <Image /> },
    { label: t("profile.admissions") || "Admissions", icon: <LocalHospital /> },
    { label: t("profile.nursing") || "Nursing", icon: <MonitorHeart /> },
    { label: t("profile.costs") || "Costs", icon: <Receipt /> },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {showFullHistory
            ? t("profile.fullPatientHistory") || "Full Patient History"
            : ticketId
              ? t("profile.ticketInformation") || "Ticket Information"
              : t("profile.patientProfile") || "Patient Profile"}
        </Typography>
        {/* Add buttons for each tab */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {selectedTab === 1 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEncounterDialogOpen(true)}
              size="small"
            >
              {t("profile.addEncounter") || "Add Consultation"}
            </Button>
          )}
          {selectedTab === 2 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNoteDialogOpen(true)}
              size="small"
            >
              {t("profile.addNote") || "Add Note"}
            </Button>
          )}
          {selectedTab === 3 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPrescription}
              size="small"
            >
              {t("profile.addPrescription") || "Add Prescription"}
            </Button>
          )}
          {selectedTab === 4 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setLabDialogOpen(true)}
              size="small"
            >
              {t("profile.addLabTest") || "Add Lab Test"}
            </Button>
          )}
          {selectedTab === 5 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setXrayDialogOpen(true)}
              size="small"
            >
              {t("profile.addXRay") || "Add X-Ray"}
            </Button>
          )}
          {selectedTab === 6 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUltrasoundDialogOpen(true)}
              size="small"
            >
              {t("profile.addUltrasound") || "Add Ultrasound"}
            </Button>
          )}
          {selectedTab === 7 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEchoDialogOpen(true)}
              size="small"
            >
              {t("profile.addEcho") || "Add Echo"}
            </Button>
          )}
        </Box>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            icon={tab.icon}
            label={tab.label}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {/* Overview Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Typography variant="h6" gutterBottom>
          {t("profile.timeline") || "Timeline"}
        </Typography>
        {Object.keys(groupedData).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t("profile.noData") || "No data available"}
          </Typography>
        ) : (
          Object.keys(groupedData).map((ticketKey) => (
            <Accordion key={ticketKey} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                    {ticketKey !== "no-ticket"
                      ? `${t("profile.ticket") || "Ticket"}: ${groupedData[ticketKey].ticket_number || ticketKey}`
                      : t("profile.noTicket") || "No Ticket"}
                  </Typography>
                  <Chip
                    label={`${Object.values(
                      groupedData[ticketKey].encounters,
                    ).reduce((sum, enc) => sum + enc.items.length, 0)} items`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {Object.keys(groupedData[ticketKey].encounters).map(
                  (encounterKey) => (
                    <Box key={encounterKey} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {encounterKey !== "no-encounter"
                          ? `${t("profile.encounter") || "Encounter"}: ${encounterKey.replace("ENC-", "")}`
                          : t("profile.noEncounter") || "No Encounter"}
                      </Typography>
                      {groupedData[ticketKey].encounters[
                        encounterKey
                      ].items.map((item) => (
                        <Card key={item.id} variant="outlined" sx={{ mb: 1 }}>
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {item.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {new Date(item.date).toLocaleString()}
                                </Typography>
                                {item.subtitle && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {item.subtitle}
                                  </Typography>
                                )}
                                {(item.data?.created_by ||
                                  item.data?.doctor ||
                                  item.data?.recorded_by ||
                                  item.data?.nurse) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {t("profile.addedBy") ||
                                      t("profile.createdBy") ||
                                      "Added by"}
                                    :{" "}
                                    {item.data.created_by ||
                                      item.data.doctor ||
                                      item.data.recorded_by ||
                                      item.data.nurse}
                                    {(item.data.created_by_username ||
                                      item.data.doctor_username ||
                                      item.data.recorded_by_username ||
                                      item.data.nurse_username) &&
                                      ` (${item.data.created_by_username || item.data.doctor_username || item.data.recorded_by_username || item.data.nurse_username})`}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                label={item.type.replace("_", " ")}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ),
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </TabPanel>

      {/* Consultations Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Typography variant="h6" gutterBottom>
          {t("profile.consultations") || "Consultations"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "encounter")
          .map((item) => (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(item.date).toLocaleString()}
                </Typography>
                {item.data?.chief_complaint && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>
                      {t("profile.chiefComplaint") || "Chief Complaint"}:
                    </strong>{" "}
                    {item.data.chief_complaint}
                  </Typography>
                )}
                {item.data?.diagnosis && (
                  <Typography variant="body2">
                    <strong>{t("profile.diagnosis") || "Diagnosis"}:</strong>{" "}
                    {item.data.diagnosis}
                  </Typography>
                )}
                {(item.data?.created_by_username ||
                  item.data?.provider_name_display) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {t("profile.addedBy") ||
                      t("profile.createdBy") ||
                      "Added by"}
                    :{" "}
                    {item.data.created_by ||
                      item.data.created_by_username ||
                      item.data.provider_name_display}
                    {item.data.created_by_username &&
                      ` (${item.data.created_by_username})`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Clinical Notes Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Typography variant="h6" gutterBottom>
          {t("profile.notes") || "Clinical Notes"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "encounter")
          .flatMap(
            (encounter) =>
              encounter.data?.clinical_notes?.map((note) => ({
                ...note,
                encounter_date: encounter.date,
              })) || [],
          )
          .map((note, idx) => (
            <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {note.note_type_display} -{" "}
                  {new Date(note.created_at).toLocaleString()}
                </Typography>
                {note.subjective && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>S:</strong> {note.subjective}
                  </Typography>
                )}
                {note.objective && (
                  <Typography variant="body2">
                    <strong>O:</strong> {note.objective}
                  </Typography>
                )}
                {note.assessment && (
                  <Typography variant="body2">
                    <strong>A:</strong> {note.assessment}
                  </Typography>
                )}
                {note.plan && (
                  <Typography variant="body2">
                    <strong>P:</strong> {note.plan}
                  </Typography>
                )}
                {note.content && !note.subjective && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {note.content}
                  </Typography>
                )}
                {(note.created_by || note.created_by_username) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {t("profile.addedBy") ||
                      t("profile.createdBy") ||
                      "Added by"}
                    : {note.created_by}
                    {note.created_by_username &&
                      ` (${note.created_by_username})`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Prescriptions Tab */}
      <TabPanel value={selectedTab} index={3}>
        <PrescriptionsList
          patientId={patientId}
          ticketId={ticketId}
          doctor={user}
        />
      </TabPanel>

      {/* Lab Tests Tab */}
      <TabPanel value={selectedTab} index={4}>
        <Typography variant="h6" gutterBottom>
          {t("profile.labTests") || "Lab Tests"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "laboratory")
          .map((item) => {
            const orderId = item.id;
            const orderDetail = labOrderDetails[orderId];
            const loadingResult = labOrderLoading[orderId];
            const testsDisplay = orderDetail?.tests_display || [];
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.result_status_display && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={item.data.result_status_display}
                        size="small"
                        color={
                          item.data.result_status === "given"
                            ? "success"
                            : item.data.result_status === "in_progress"
                              ? "info"
                              : item.data.result_status === "cancelled"
                                ? "default"
                                : "warning"
                        }
                        variant="outlined"
                      />
                    </Box>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!orderDetail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loadingResult ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchLabOrderResults(orderId)}
                      disabled={loadingResult}
                      sx={{ mt: 2 }}
                    >
                      {loadingResult
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ mb: 1 }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "grey.100" }}>
                              <TableCell>
                                <strong>
                                  {t("profile.tests", "Test(s)")}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>
                                  {t("profile.result", "Result")}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>
                                  {t("profile.normalRange", "Normal range")}
                                </strong>
                              </TableCell>
                              <TableCell>
                                <strong>
                                  {t("profile.units", "Units")}
                                </strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {testsDisplay.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={4}
                                  align="center"
                                  color="text.secondary"
                                >
                                  {t("profile.noResults", "No tests in this order.")}
                                </TableCell>
                              </TableRow>
                            ) : (
                              testsDisplay.map((test) => {
                                const tt = test.test_type_display || {};
                                const hasResult =
                                  tt.result_value != null &&
                                  tt.result_value !== "";
                                const isParent =
                                  !test.parent_test &&
                                  test.child_tests?.length > 0;
                                const resultDisplay = hasResult
                                  ? tt.result_value
                                  : isParent
                                    ? "—"
                                    : t("profile.pending", "Pending") ||
                                      "Pending";
                                const normalRange =
                                  tt.reference_range ||
                                  (tt.min_value != null && tt.max_value != null
                                    ? `${tt.min_value} - ${tt.max_value}`
                                    : null) ||
                                  "—";
                                return (
                                  <TableRow key={test.id}>
                                    <TableCell
                                      sx={
                                        isParent
                                          ? { fontWeight: "bold" }
                                          : undefined
                                      }
                                    >
                                      {tt.name || "—"}
                                    </TableCell>
                                    <TableCell>{resultDisplay}</TableCell>
                                    <TableCell>{normalRange}</TableCell>
                                    <TableCell>{tt.units || "—"}</TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* X-Ray Tab */}
      <TabPanel value={selectedTab} index={5}>
        <Typography variant="h6" gutterBottom>
          {t("profile.xray") || "X-Ray"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "xray")
          .map((item) => {
            const key = getDiagnosticKey("xray", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const resultData = detail?.result_data;
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.xray_type && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.type") || "Type"}:</strong>{" "}
                      {item.data.xray_type}
                    </Typography>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchDiagnosticResult("xray", item.id)}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      {resultData?.findings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.findings", "Findings")}:</strong>{" "}
                          {resultData.findings}
                        </Typography>
                      )}
                      {resultData?.image && (
                        <Box
                          sx={{ mt: 1, cursor: "pointer" }}
                          onClick={() => {
                            setImageViewerSrc(
                              toAbsoluteMediaUrl(resultData.image)
                            );
                            setImageViewerTitle(
                              t("profile.xray", "X-Ray") +
                                (item.data?.xray_type
                                  ? ` - ${item.data.xray_type}`
                                  : "")
                            );
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={toAbsoluteMediaUrl(resultData.image)}
                            alt="X-Ray"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 300,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                      {!resultData?.findings && !resultData?.image && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.noResults", "No results available.")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Ultrasound Tab */}
      <TabPanel value={selectedTab} index={6}>
        <Typography variant="h6" gutterBottom>
          {t("profile.ultrasound") || "Ultrasound"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "ultrasound")
          .map((item) => {
            const key = getDiagnosticKey("ultrasound", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const resultData = detail?.result_data;
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.ultrasound_type && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.type") || "Type"}:</strong>{" "}
                      {item.data.ultrasound_type}
                    </Typography>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() =>
                        fetchDiagnosticResult("ultrasound", item.id)
                      }
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      {resultData?.findings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.findings", "Findings")}:</strong>{" "}
                          {resultData.findings}
                        </Typography>
                      )}
                      {resultData?.image && (
                        <Box
                          sx={{ mt: 1, cursor: "pointer" }}
                          onClick={() => {
                            setImageViewerSrc(
                              toAbsoluteMediaUrl(resultData.image)
                            );
                            setImageViewerTitle(
                              t("profile.ultrasound", "Ultrasound") +
                                (item.data?.ultrasound_type
                                  ? ` - ${item.data.ultrasound_type}`
                                  : "")
                            );
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={toAbsoluteMediaUrl(resultData.image)}
                            alt="Ultrasound"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 300,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                      {!resultData?.findings && !resultData?.image && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.noResults", "No results available.")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Echo Tab */}
      <TabPanel value={selectedTab} index={7}>
        <Typography variant="h6" gutterBottom>
          {t("profile.echo") || "Echo"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "echo")
          .map((item) => {
            const key = getDiagnosticKey("echo", item.id);
            const detail = diagnosticDetails[key];
            const loading = diagnosticLoading[key];
            const resultData = detail?.result_data;
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                  {item.data?.echo_type && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t("profile.type") || "Type"}:</strong>{" "}
                      {item.data.echo_type}
                    </Typography>
                  )}
                  {(item.data?.created_by || item.data?.doctor_username) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {t("profile.addedBy") ||
                        t("profile.createdBy") ||
                        "Added by"}
                      : {item.data.created_by || item.data.doctor}
                      {(item.data.created_by_username ||
                        item.data.doctor_username) &&
                        ` (${item.data.created_by_username || item.data.doctor_username})`}
                    </Typography>
                  )}
                  {!detail ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ViewResultIcon />
                        )
                      }
                      onClick={() => fetchDiagnosticResult("echo", item.id)}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading
                        ? t("profile.loading", "Loading…")
                        : t("profile.viewResult", "View result")}
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: "bold" }}
                      >
                        {t("profile.results", "Results")}
                      </Typography>
                      {resultData?.findings && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{t("profile.findings", "Findings")}:</strong>{" "}
                          {resultData.findings}
                        </Typography>
                      )}
                      {resultData?.image && (
                        <Box
                          sx={{ mt: 1, cursor: "pointer" }}
                          onClick={() => {
                            setImageViewerSrc(
                              toAbsoluteMediaUrl(resultData.image)
                            );
                            setImageViewerTitle(
                              t("profile.echo", "Echo") +
                                (item.data?.echo_type
                                  ? ` - ${item.data.echo_type}`
                                  : "")
                            );
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={toAbsoluteMediaUrl(resultData.image)}
                            alt="Echo"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 300,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}
                      {!resultData?.findings && !resultData?.image && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.noResults", "No results available.")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          }) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Admissions Tab */}
      <TabPanel value={selectedTab} index={8}>
        <Typography variant="h6" gutterBottom>
          {t("profile.admissions") || "Admissions"}
        </Typography>
        {ehrData?.timeline
          ?.filter(
            (item) =>
              item.type === "ipd_admission" || item.type === "icu_admission",
          )
          .map((item) => (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(item.date).toLocaleString()}
                </Typography>
                {item.data?.ward && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>{t("profile.ward") || "Ward"}:</strong>{" "}
                    {item.data.ward}
                  </Typography>
                )}
                {item.data?.bed && (
                  <Typography variant="body2">
                    <strong>{t("profile.bed") || "Bed"}:</strong>{" "}
                    {item.data.bed}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Nursing Tab - Vital Signs & nursing data */}
      <TabPanel value={selectedTab} index={9}>
        <Typography variant="h6" gutterBottom>
          {t("profile.nursing") || "Nursing"} –{" "}
          {t("profile.vitalSigns") || "Vital Signs"}
        </Typography>
        {ehrData?.timeline
          ?.filter((item) => item.type === "vital_signs")
          .map((item) => (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {new Date(item.date).toLocaleString()}
                  {item.data?.recorded_by &&
                    ` · ${t("profile.recordedBy") || "Recorded by"}: ${item.data.recorded_by}`}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {item.data?.temperature != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.temperature") || "Temp"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.temperature} °C
                      </Typography>
                    </Grid>
                  )}
                  {(item.data?.blood_pressure_systolic != null ||
                    item.data?.blood_pressure_diastolic != null) && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.bloodPressure") || "BP"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.blood_pressure_systolic ?? "—"}/
                        {item.data.blood_pressure_diastolic ?? "—"} mmHg
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.heart_rate != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.heartRate") || "HR"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.heart_rate} bpm
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.respiratory_rate != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.respiratoryRate") || "RR"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.respiratory_rate} /min
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.oxygen_saturation != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.spO2") || "SpO2"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.oxygen_saturation}%
                      </Typography>
                    </Grid>
                  )}
                  {item.data?.weight != null && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t("profile.weight") || "Weight"}
                      </Typography>
                      <Typography variant="body2">
                        {item.data.weight} kg
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {item.data?.notes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {item.data.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )) || (
          <Typography variant="body2" color="text.secondary">
            {t("profile.noData") || "No data available"}
          </Typography>
        )}
      </TabPanel>

      {/* Costs Tab */}
      <TabPanel value={selectedTab} index={10}>
        <CostSummary patientId={patientId} />
      </TabPanel>

      {/* All Dialogs */}
      {prescriptionDialogOpen && (
        <PrescriptionDialog
          open={prescriptionDialogOpen}
          onClose={handlePrescriptionDialogClose}
          patient={patientId != null ? { id: patientId } : undefined}
          initialPatientId={patientId}
          doctor={user}
          ticketId={ticketId}
        />
      )}

      <LabTestDialog
        open={labDialogOpen}
        onClose={() => {
          setLabDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={ticketId}
      />

      <XRayDialog
        open={xrayDialogOpen}
        onClose={() => {
          setXrayDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={ticketId}
      />

      <EchoDialog
        open={echoDialogOpen}
        onClose={() => {
          setEchoDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={ticketId}
      />

      <UltrasoundDialog
        open={ultrasoundDialogOpen}
        onClose={() => {
          setUltrasoundDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={ticketId}
      />

      <EncounterDialog
        open={encounterDialogOpen}
        onClose={() => {
          setEncounterDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={ticketId}
      />

      <ClinicalNoteDialog
        open={noteDialogOpen}
        onClose={() => {
          setNoteDialogOpen(false);
          setTimeout(() => refetch(), 1000);
        }}
        patientId={patientId}
        doctorId={user?.id}
        ticketId={ticketId}
      />

      <ZoomableImageDialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageSrc={imageViewerSrc}
        title={imageViewerTitle}
      />
    </Box>
  );
};

export default ComprehensiveProfile;
